import type { Logger } from '../core/services';

/**
 * The monitor bus's way to the speakers (docs/browser-limitations.md, LIM-AUD-06).
 *
 * The monitor stream is rendered by a second AudioContext whose output device is
 * chosen with `AudioContext.setSinkId`. It used to play through an `<audio>`
 * element with `srcObject`; Chromium's media-element path for a MediaStream
 * builds up latency over time (seconds after enough pause/play cycles or
 * throttling), which put the monitor seconds behind the preview. An
 * AudioContext renders a MediaStream with a fixed, small buffer and does not
 * drift.
 *
 * Keeping the device on its own context also keeps an output-device failure
 * away from the context that clocks the program output: only this one
 * suspends, and it is resumed again from its own statechange and on the
 * engine's retry moments.
 *
 * Browsers without `AudioContext.setSinkId` (Firefox, Safari) monitor on the
 * system default device; the settings' device choice is ignored there with one
 * warning.
 */
const LOG_SCOPE = 'audio';
/**
 * `resume()` stays pending for as long as the browser withholds permission;
 * the transition queue moves on after this long and the context's own
 * statechange re-runs the reconciliation once the browser grants it.
 */
const RESUME_TIMEOUT_MS = 1000;

/** `AudioContext.setSinkId` (Chromium 110+); TypeScript's DOM lib does not declare it yet. */
export interface SinkAudioContext extends AudioContext {
  setSinkId?(sinkId: string | { type: 'none' }): Promise<void>;
  readonly sinkId?: string | { type: 'none' };
}

export interface MonitorOutputDeps {
  logger: Logger;
  /** Creates the context that renders the monitor stream. */
  createContext(options: AudioContextOptions): SinkAudioContext;
  /** Connects a stream into the given context; returns the node to wire to its destination. */
  createSource(context: SinkAudioContext, stream: MediaStream): AudioNode;
  listOutputs(): Promise<MediaDeviceInfo[]>;
}

function appliedId(context: SinkAudioContext): string {
  const id = context.sinkId;
  return typeof id === 'string' ? id : '';
}

function withTimeout(promise: Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    promise.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export class MonitorOutput {
  private readonly context: SinkAudioContext;
  private readonly source: AudioNode;
  /** The device the settings ask for ('' = system default). */
  private wantedSinkId = '';
  /** The device the context was last told to use; differs from the wanted one while that is missing. */
  private appliedSinkId = '';
  private sinkWarningLogged = false;
  private resumeWarningLogged = false;
  /** Target state; the context is reconciled towards it after every settled transition. */
  private wanted = false;
  /**
   * resume()/suspend() settle asynchronously and `context.state` only changes
   * when they do, so transitions are queued and each one re-reads the target
   * once the previous has settled.
   */
  private queue: Promise<void> = Promise.resolve();
  private closed = false;

  constructor(
    private readonly deps: MonitorOutputDeps,
    stream: MediaStream,
    sampleRate: number,
  ) {
    this.context = deps.createContext({ sampleRate, latencyHint: 'interactive' });
    this.source = deps.createSource(this.context, stream);
    this.source.connect(this.context.destination);
    this.context.addEventListener('error', this.handleError);
    this.context.addEventListener('statechange', this.handleStateChange);
  }

  /** The device is only kept open while something is actually routed to it. */
  update(wanted: boolean): void {
    if (this.closed) return;
    this.wanted = wanted;
    this.reconcile();
  }

  setDevice(sinkId: string): void {
    if (this.closed || sinkId === this.wantedSinkId) return;
    this.wantedSinkId = sinkId;
    this.applySink(sinkId);
  }

  /**
   * After a device change: move the monitor to the system default if its
   * device is gone, and back once it returns.
   */
  async reconcileDevice(): Promise<void> {
    const wanted = this.wantedSinkId;
    if (wanted === '' || typeof this.context.setSinkId !== 'function') return;
    let devices: MediaDeviceInfo[];
    try {
      devices = await this.deps.listOutputs();
    } catch {
      return;
    }
    if (this.closed || wanted !== this.wantedSinkId) return;
    const outputs = devices.filter((device) => device.kind === 'audiooutput');
    // Without device permission the browser blanks the ids and presence cannot be judged.
    if (!outputs.some((device) => device.deviceId !== '')) return;

    if (outputs.some((device) => device.deviceId === wanted)) {
      if (this.appliedSinkId !== wanted) {
        this.deps.logger.log('info', LOG_SCOPE, `Monitor device "${wanted}" is available again; switching back to it`);
        this.applySink(wanted);
      }
    } else if (this.appliedSinkId !== '') {
      this.deps.logger.log(
        'warn',
        LOG_SCOPE,
        `Monitor device "${wanted}" disappeared; monitoring falls back to the system default device`,
      );
      this.applySink('');
    }
    this.reconcile();
  }

  async destroy(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.context.removeEventListener('error', this.handleError);
    this.context.removeEventListener('statechange', this.handleStateChange);
    this.source.disconnect();
    if (this.context.state !== 'closed') await this.context.close();
  }

  private reconcile(): void {
    this.queue = this.queue.then(() => this.transition()).catch(() => undefined);
  }

  private async transition(): Promise<void> {
    if (this.closed) return;
    const context = this.context;
    if (this.wanted) {
      if (context.state !== 'suspended' && context.state !== 'interrupted') return;
      try {
        await withTimeout(context.resume(), RESUME_TIMEOUT_MS);
        this.resumeWarningLogged = false;
      } catch (error) {
        if (!this.resumeWarningLogged) {
          this.resumeWarningLogged = true;
          this.deps.logger.log('warn', LOG_SCOPE, 'Monitor playback could not start', error);
        }
      }
    } else if (context.state === 'running') {
      await context.suspend().catch(() => undefined);
    }
  }

  private applySink(sinkId: string): void {
    if (sinkId === this.appliedSinkId) return;
    this.appliedSinkId = sinkId;
    const context = this.context;
    if (typeof context.setSinkId !== 'function') {
      if (sinkId !== '' && !this.sinkWarningLogged) {
        this.sinkWarningLogged = true;
        this.deps.logger.log(
          'warn',
          LOG_SCOPE,
          'This browser cannot select an audio output device; monitoring plays on the system default device',
        );
      }
      return;
    }
    context.setSinkId(sinkId).catch((error: unknown) => {
      if (this.closed) return;
      // Forget the failed id so the same device can be tried again once it is back.
      if (this.appliedSinkId === sinkId) this.appliedSinkId = appliedId(context);
      this.deps.logger.log('warn', LOG_SCOPE, `Monitor device "${sinkId}" is unavailable; keeping the previous device`, error);
    });
  }

  private readonly handleError = (): void => {
    // Chromium reports a failing output device here and then suspends the context, which handleStateChange picks up.
    this.deps.logger.log('warn', LOG_SCOPE, 'The monitor output device reported a rendering error');
  };

  /** Our own transitions and the browser's (device failure, interruption, a granted late resume) end up here. */
  private readonly handleStateChange = (): void => {
    this.reconcile();
  };
}

export function createMonitorOutput(logger: Logger, stream: MediaStream, sampleRate: number): MonitorOutput {
  return new MonitorOutput(
    {
      logger,
      createContext: (options) => new AudioContext(options),
      createSource: (context, source) => new MediaStreamAudioSourceNode(context, { mediaStream: source }),
      listOutputs: () => navigator.mediaDevices.enumerateDevices(),
    },
    stream,
    sampleRate,
  );
}
